// GM/Master "Report" export — one workbook, two tabs:
//   "S.A performance" — one row per Date + Workshop + Advisor + Service Type
//   "Escalations"      — one row per escalation incident
// Both built from the same group/step/escalation logic Overview and the Escalations
// page already use. Left out on purpose, no honest data source yet:
//   - Dealer (no dealer concept in the schema)
//   - Avg Job Card Closure Time (groups.actual_delivered_at is never written)
//   - Resolved On / TAT (escalation "close" isn't persisted to the DB yet)
// See docs/backend-integration.md item 8a and the Escalations page's overrides-only
// close/assign state for why.

import XLSX from "xlsx-js-style";
import { format } from "date-fns";
import { nb, isEscalationFlagged } from "./journey";
import { isPendingResponse, pendingApprovalStage, isTerminalStatus, isSent, buildEscalations } from "./queueMetrics";
import { parseCreatedAt } from "./dates";
import { computeCSAT, parseRecommendationScore } from "./feedback";
import { buildAdvisorMetrics } from "./advisorMetrics";

// Same Promoter/Dissatisfied split as feedback.js's computeNPS, but reports the true
// (possibly negative) score instead of flooring at 0 — that floor is a dashboard display
// rule, not something this report should silently inherit.
function computeRawNPS(feedbackRows) {
  const scores = (feedbackRows || []).map((r) => parseRecommendationScore(r["Recommendation Score (1-10)"])).filter((n) => n != null);
  const total = scores.length;
  if (!total) return null;
  const promoters = scores.filter((s) => s >= 9).length;
  const dissatisfied = total - promoters;
  return Math.round(((promoters - dissatisfied) / total) * 100);
}

const SA_COLUMNS = [
  "Date",
  "Workshop",
  "Advisor",
  "Service Type",
  "Job Cards Open",
  "Job Cards Closed",
  "Pending Initiations",
  "Pending Responses",
  "Escalations",
  "Job Cards Closed per Advisor",
];

const ESCALATION_COLUMNS = ["Customer Name", "Reason", "Group Name", "Contact", "Advisor", "Workshop", "Status", "Raised On"];

const isJobCardSent = (g) => isSent(g["Job Card Sent"]);
const isJobCardClosed = (g) => nb(g["Feedback Notified"]);
const isPendingInitiation = (g) => !isJobCardSent(g) && !isTerminalStatus(g["Status"]);

function groupKey(g, dateLabel) {
  return [dateLabel, g["Workshop Location"] || "—", g["Service Advisor Name"] || "—", g["Service Type"] || "—"].join("|||");
}

// `groups` should already be scoped to the viewer's workshop(s) and date range —
// this only aggregates, it doesn't apply any scoping of its own.
export function buildGmReportRows(groups) {
  const buckets = new Map();

  groups.forEach((g) => {
    const createdAt = parseCreatedAt(g);
    const dateLabel = createdAt ? format(createdAt, "dd-MMM-yyyy") : "—";
    const key = groupKey(g, dateLabel);

    if (!buckets.has(key)) {
      buckets.set(key, {
        Date: dateLabel,
        Workshop: g["Workshop Location"] || "—",
        Advisor: g["Service Advisor Name"] || "—",
        "Service Type": g["Service Type"] || "—",
        "Job Cards Open": 0,
        "Job Cards Closed": 0,
        "Pending Initiations": 0,
        "Pending Responses": 0,
        "Escalations": 0,
        "Job Cards Closed per Advisor": 0,
      });
    }
    const row = buckets.get(key);

    const closed = isJobCardClosed(g);
    if (closed) {
      row["Job Cards Closed"] += 1;
      row["Job Cards Closed per Advisor"] += 1;
    } else if (isJobCardSent(g)) {
      row["Job Cards Open"] += 1;
    }
    if (isPendingInitiation(g)) row["Pending Initiations"] += 1;
    if (isPendingResponse(g)) row["Pending Responses"] += 1;
    if (isEscalationFlagged(g["Escalation Flag"])) row["Escalations"] += 1;
  });

  return [...buckets.values()].sort((a, b) => a.Date.localeCompare(b.Date) || a.Workshop.localeCompare(b.Workshop) || a.Advisor.localeCompare(b.Advisor));
}

// `groups` scopes which incidents show up (same list passed to buildGmReportRows);
// `sentimentRows` is the full sentiment_history set — buildEscalations joins it back
// by group_row_id, so scoping is entirely controlled by `groups`.
export function buildEscalationReportRows(groups, sentimentRows) {
  const incidents = buildEscalations(groups, sentimentRows || []);
  const groupsByRow = new Map(groups.map((g) => [g._row, g]));

  return incidents
    .map((e) => {
      const g = groupsByRow.get(e.groupRowId);
      return {
        "Customer Name": e.customerName,
        Reason: e.reasonText,
        "Group Name": e.vehicleReg,
        Contact: g?.["Customer Number"] || "—",
        Advisor: e.advisorName,
        Workshop: e.workshopLocation,
        Status: e.status === "closed" ? "Closed" : "Open",
        "Raised On": e.raisedAt ? format(new Date(e.raisedAt), "dd-MMM-yyyy HH:mm") : "—",
      };
    })
    .sort((a, b) => b["Raised On"].localeCompare(a["Raised On"]));
}

const WORKSHOP_COLUMNS = [
  "Date",
  "Workshop",
  "Service Type",
  "JC Status",
  "Services Booked",
  "Groups Created",
  "Job Cards Created",
  "Job Cards Open",
  "Job Cards Closed",
  "Job Card Closure Rate (%)",
  "Feedback Collected",
  "Positive Feedback",
  "Neutral Feedback",
  "Negative Feedback",
  "NPS Score",
  "Escalations",
  "Pending Customer Approval",
  "Pending Responses to Customer Messages",
];

function workshopGroupKey(g, dateLabel) {
  return [dateLabel, g["Workshop Location"] || "—", g["Service Type"] || "—", g["Status"] || "—"].join("|||");
}

// `groups` and `feedbackRows` should already be scoped to the viewer's workshop(s) and
// date range. Rows here are Date + Workshop + Service Type + JC Status (the group's raw
// pipeline "Status" column, e.g. Created / In Progress / Delivered).
export function buildWorkshopSummaryRows(groups, feedbackRows) {
  const buckets = new Map();

  groups.forEach((g) => {
    const createdAt = parseCreatedAt(g);
    const dateLabel = createdAt ? format(createdAt, "dd-MMM-yyyy") : "—";
    const key = workshopGroupKey(g, dateLabel);

    if (!buckets.has(key)) {
      buckets.set(key, {
        Date: dateLabel,
        Workshop: g["Workshop Location"] || "—",
        "Service Type": g["Service Type"] || "—",
        "JC Status": g["Status"] || "—",
        groupRows: [],
      });
    }
    buckets.get(key).groupRows.push(g);
  });

  return [...buckets.values()]
    .map((bucket) => {
      const rows = bucket.groupRows;
      const groupRowIds = new Set(rows.map((g) => g._row));
      const bucketFeedback = (feedbackRows || []).filter((f) => groupRowIds.has(f["Group Row Id"]));

      const jobCardsCreated = rows.filter(isJobCardSent).length;
      const jobCardsClosed = rows.filter(isJobCardClosed).length;
      const jobCardsOpen = rows.filter((g) => isJobCardSent(g) && !isJobCardClosed(g)).length;
      const csat = computeCSAT(bucketFeedback);
      const npsScore = computeRawNPS(bucketFeedback);

      return {
        Date: bucket.Date,
        Workshop: bucket.Workshop,
        "Service Type": bucket["Service Type"],
        "JC Status": bucket["JC Status"],
        "Services Booked": rows.filter((g) => nb(g["Service Type"])).length,
        "Groups Created": rows.length,
        "Job Cards Created": jobCardsCreated,
        "Job Cards Open": jobCardsOpen,
        "Job Cards Closed": jobCardsClosed,
        "Job Card Closure Rate (%)": jobCardsCreated ? Math.round((jobCardsClosed / jobCardsCreated) * 100) : 0,
        "Feedback Collected": bucketFeedback.length,
        "Positive Feedback": csat.sentimentCounts.Positive,
        "Neutral Feedback": csat.sentimentCounts.Neutral,
        "Negative Feedback": csat.sentimentCounts.Negative,
        "NPS Score": npsScore ?? "—",
        Escalations: rows.filter((g) => isEscalationFlagged(g["Escalation Flag"])).length,
        "Pending Customer Approval": rows.filter((g) => pendingApprovalStage(g) !== null).length,
        "Pending Responses to Customer Messages": rows.filter(isPendingResponse).length,
      };
    })
    .sort((a, b) => a.Date.localeCompare(b.Date) || a.Workshop.localeCompare(b.Workshop) || a["Service Type"].localeCompare(b["Service Type"]));
}

const ADVISOR_TOTALS_COLUMNS = [
  "Workshop",
  "Advisor",
  "Job Cards Total",
  "Job Cards Closed",
  "Job Cards Open",
  "Closure Rate (%)",
  "Adherence (%)",
  "CSAT (%)",
  "Median Reply Time (mins)",
  "Escalations",
  "Pending Responses",
];

const normalizeAdvisorName = (v) => String(v || "").trim().toLowerCase();

// One row per advisor, totaled across the whole scoped period — the complement to
// "S.A performance", which splits the same data across Date + Service Type and gets
// noisy fast once a master is looking across many workshops at once.
export function buildAdvisorTotalsRows(groups, feedbackRows, messageLogRows) {
  const advisorMetrics = buildAdvisorMetrics(groups, feedbackRows, messageLogRows);

  const extrasByKey = new Map();
  groups.forEach((g) => {
    const name = g["Service Advisor Name"];
    if (!nb(name)) return;
    const key = normalizeAdvisorName(name);
    if (!extrasByKey.has(key)) extrasByKey.set(key, { open: 0, pendingResponses: 0, escalations: 0 });
    const extras = extrasByKey.get(key);
    if (isJobCardSent(g) && !isJobCardClosed(g)) extras.open += 1;
    if (isPendingResponse(g)) extras.pendingResponses += 1;
    if (isEscalationFlagged(g["Escalation Flag"])) extras.escalations += 1;
  });

  return advisorMetrics
    .map((a) => {
      const extras = extrasByKey.get(normalizeAdvisorName(a.advisorName)) || { open: 0, pendingResponses: 0, escalations: 0 };
      return {
        Workshop: a.workshopLocation || "—",
        Advisor: a.advisorName,
        "Job Cards Total": a.jobCardsTotal,
        "Job Cards Closed": a.jobCardsClosed,
        "Job Cards Open": extras.open,
        "Closure Rate (%)": a.jobCardsTotal ? Math.round((a.jobCardsClosed / a.jobCardsTotal) * 100) : 0,
        "Adherence (%)": a.adherencePct ?? "—",
        "CSAT (%)": a.csatPct ?? "—",
        "Median Reply Time (mins)": a.medianResponseMins != null ? Math.round(a.medianResponseMins) : "—",
        Escalations: extras.escalations,
        "Pending Responses": extras.pendingResponses,
      };
    })
    .sort((a, b) => a.Workshop.localeCompare(b.Workshop) || a.Advisor.localeCompare(b.Advisor));
}

const HEADER_STYLE = {
  fill: { fgColor: { rgb: "2563EB" } },
  font: { color: { rgb: "FFFFFF" }, bold: true },
  alignment: { horizontal: "center", vertical: "center" },
};

function addSheet(workbook, rows, columns, sheetName) {
  const sheet = XLSX.utils.json_to_sheet(rows, { header: columns });
  columns.forEach((_, i) => {
    const cellRef = XLSX.utils.encode_cell({ r: 0, c: i });
    if (sheet[cellRef]) sheet[cellRef].s = HEADER_STYLE;
  });
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName);
}

export function downloadGmReportXlsx(saRows, escalationRows, workshopRows, advisorTotalsRows, filenamePrefix = "gm-report") {
  const workbook = XLSX.utils.book_new();
  addSheet(workbook, advisorTotalsRows, ADVISOR_TOTALS_COLUMNS, "S.A overall");
  addSheet(workbook, saRows, SA_COLUMNS, "S.A performance");
  addSheet(workbook, escalationRows, ESCALATION_COLUMNS, "Escalations");
  addSheet(workbook, workshopRows, WORKSHOP_COLUMNS, "Workshop Summary");

  // Colons aren't valid in Windows filenames, so use dashes for the time portion.
  const stamp = format(new Date(), "yyyy-MM-dd_HH-mm-ss");
  XLSX.writeFile(workbook, `${filenamePrefix}-${stamp}.xlsx`);
}
